import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { Observable, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserPortalService } from '../services/user-portal.service';

@Component({
  selector: 'app-refund-status',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './refund-status.component.html',
  styleUrls: ['./refund-status.component.scss']
})
export class RefundStatusComponent implements OnInit, OnDestroy {
  private auth = inject(Auth);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private portalService = inject(UserPortalService);

  user$ = user(this.auth);
  refundRequests: any[] = [];
  selectedRequest: any = null;
  isLoading = true;
  copiedCode = false;

  // Attached files state
  evidenceManifests: any[] = [];
  evidenceUrls: { [attachmentId: string]: string } = {};
  isLoadingEvidence = false;
  activeLightboxUrl: string | null = null;
  activeLightboxTitle: string | null = null;

  private sub?: Subscription;
  private evidenceSub?: Subscription;

  get userEmail(): string {
    return this.auth.currentUser?.email || localStorage.getItem('userEmail') || '';
  }

  ngOnInit(): void {
    this.sub = this.user$.pipe(
      switchMap(u => {
        const email = u?.email || localStorage.getItem('userEmail') || '';
        if (email) {
          return this.portalService.getUserRefundRequests(email);
        }
        return of([]);
      })
    ).subscribe(requests => {
      this.refundRequests = requests || [];
      this.isLoading = false;

      // Handle selection based on query or route params
      const targetId = this.route.snapshot.queryParamMap.get('id') || this.route.snapshot.paramMap.get('requestId');
      if (targetId) {
        this.selectedRequest = this.refundRequests.find(r => r.id === targetId) || this.refundRequests[0] || null;
      } else if (this.refundRequests.length === 1) {
        this.selectedRequest = this.refundRequests[0];
      } else if (this.selectedRequest) {
        // Keep updated record
        this.selectedRequest = this.refundRequests.find(r => r.id === this.selectedRequest.id) || this.selectedRequest;
      }

      if (this.selectedRequest) {
        this.loadAttachedFilesForSelectedRequest();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.evidenceSub?.unsubscribe();
  }

  selectRequest(request: any): void {
    this.selectedRequest = request;
    this.loadAttachedFilesForSelectedRequest();
  }

  clearSelection(): void {
    this.selectedRequest = null;
    this.evidenceSub?.unsubscribe();
    this.evidenceManifests = [];
    this.evidenceUrls = {};
    this.closeLightbox();
  }

  loadAttachedFilesForSelectedRequest(): void {
    this.evidenceSub?.unsubscribe();
    this.evidenceManifests = [];
    this.evidenceUrls = {};

    if (!this.selectedRequest?.id) return;

    this.isLoadingEvidence = true;
    this.evidenceSub = this.portalService.getRefundEvidenceManifests(this.selectedRequest.id).subscribe({
      next: (manifests) => {
        this.evidenceManifests = manifests || [];
        this.isLoadingEvidence = false;

        // Fetch view URL for each manifest
        for (const m of this.evidenceManifests) {
          if (!this.evidenceUrls[m.id]) {
            this.portalService.getRefundEvidenceUrl(this.selectedRequest.id, m.id, 'thumbnail').then(res => {
              if (res?.downloadUrl) {
                this.evidenceUrls[m.id] = res.downloadUrl;
              }
            }).catch(err => {
              console.warn('[RefundStatus] Could not fetch preview URL for attachment:', m.id, err);
            });
          }
        }
      },
      error: (err) => {
        console.warn('[RefundStatus] Error loading evidence manifests:', err);
        this.isLoadingEvidence = false;
      }
    });
  }

  openFileLightbox(manifest: any): void {
    if (!this.selectedRequest?.id || !manifest?.id) return;
    this.activeLightboxTitle = manifest.originalFilename || 'Uploaded File';
    this.activeLightboxUrl = this.evidenceUrls[manifest.id] || null;

    // Fetch full high-res URL
    this.portalService.getRefundEvidenceUrl(this.selectedRequest.id, manifest.id, 'full').then(res => {
      if (res?.downloadUrl) {
        this.activeLightboxUrl = res.downloadUrl;
      }
    }).catch(err => {
      console.warn('[RefundStatus] Could not fetch full file view URL:', err);
    });
  }

  closeLightbox(): void {
    this.activeLightboxUrl = null;
    this.activeLightboxTitle = null;
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }


  copyCouponCode(code: string): void {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.copiedCode = true;
      setTimeout(() => {
        this.copiedCode = false;
      }, 2500);
    });
  }

  formatDate(timestamp: any): string {
    if (!timestamp) return '-';
    if (typeof timestamp === 'string') {
      try {
        return new Date(timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } catch {
        return timestamp;
      }
    }
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return '-';
  }
}

